import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap, throwError } from 'rxjs';

import { DownloadableBlob, excelToJson } from '@ps/utils';
import {
  locationTemplateFileName,
  locationTemplatePath,
  LOCATION_UPLOAD_ACCEPTED_EXTENSION,
  LOCATION_UPLOAD_MESSAGES,
  LOCATION_UPLOAD_ROW_LIMIT,
  SurveyLocationGroup,
  SurveyLocationUploadResult,
} from '@ps/survey-utils';

import { LocationApi } from '../../api/location/location.api';

@Injectable({ providedIn: 'root' })
export class LocationUploadService {
  private readonly _api = inject(LocationApi);
  private readonly _document = inject(DOCUMENT);
  private readonly _http = inject(HttpClient);

  public uploadLocationCodes(
    countryCode: string,
    surveyId: number,
    qualificationCode: number,
    file: File,
  ): Observable<SurveyLocationUploadResult> {
    return this._withinRowLimit(file).pipe(
      switchMap(() =>
        this._api.uploadLocationCodes(
          countryCode,
          surveyId,
          qualificationCode,
          file,
        ),
      ),
    );
  }

  public locationGroups(
    surveyId: number,
    qualificationCode: number,
  ): Observable<SurveyLocationGroup[]> {
    return this._api.locationGroups(surveyId, qualificationCode);
  }

  public downloadTemplateFile(
    countryCode: string,
    qualificationCode: number,
  ): Observable<void> {
    return this._http
      .get(locationTemplatePath(countryCode, qualificationCode), {
        responseType: 'blob',
      })
      .pipe(
        map((blob) => {
          new DownloadableBlob(
            blob,
            locationTemplateFileName(countryCode, qualificationCode),
            this._document,
          ).download();
        }),
      );
  }

  public downloadValidatedFile(
    filePath: string,
    correctCodes: string[],
  ): Observable<void> {
    return this._api.validatedLocationFileName(filePath, correctCodes).pipe(
      switchMap((fileName) =>
        this._api
          .downloadReportFile(fileName)
          .pipe(map((blob) => ({ blob, fileName }))),
      ),
      map(({ blob, fileName }) => {
        new DownloadableBlob(blob, fileName, this._document).download();
      }),
    );
  }

  private _withinRowLimit(file: File): Observable<void> {
    if (!file.name.toLowerCase().endsWith(LOCATION_UPLOAD_ACCEPTED_EXTENSION)) {
      return throwError(
        () => new Error(LOCATION_UPLOAD_MESSAGES.FILE_REQUIRED),
      );
    }

    return excelToJson(file).pipe(
      switchMap((rows) =>
        rows.length > LOCATION_UPLOAD_ROW_LIMIT
          ? throwError(
              () => new Error(LOCATION_UPLOAD_MESSAGES.ROW_LIMIT_EXCEEDED),
            )
          : of(undefined),
      ),
    );
  }
}
